'use client';

import { Box, Typography, Button, Container, Paper } from '@mui/material';
import { LoginOutlined } from '@mui/icons-material';
import { useAuthStore } from '@/stores/authStore';
import { useConfigStore } from '@/stores/configStore';
import signedInOrAnonymousAllowed from '@/components/auth/signedInOrAnonymousAllowed';
import { navigateToShibbolethSignIn } from '@/lib/auth/signInFlow';
import { log } from '@/lib/utils/logger';

function HomePage() {
  const { currentUser, login, isInProgress } = useAuthStore();
  const { systemConfig: configStoreSystemConfig } = useConfigStore();

  const isSignedIn = currentUser && !currentUser.isAnonymous;

  const handleSignIn = async () => {
    try {
      await login();
    } catch (error) {
      log.error('[HomePage] Login failed:', error);
    }
  };

  const handleShibbolethSignin = () => {
    navigateToShibbolethSignIn(configStoreSystemConfig?.apiEndpoint, '/');
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Welcome to LiST
        </Typography>

        {isSignedIn ? (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              Hello, {currentUser.fullName}!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Role: {currentUser.roles.find((role) => role.id === currentUser.selectedRole)?.name || 'Unknown'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              You&apos;re signed in and ready to access your data and projects.
            </Typography>
          </Box>
        ) : (
          <Paper
            elevation={3}
            sx={{
              p: 4,
              mt: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Typography variant="h6" component="h2" gutterBottom align="center">
              Welcome, Community Users!
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 2 }}>
              You can explore public data and research projects, or sign in to access your personal data if you have an
              account.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: 300 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSignIn}
                disabled={isInProgress}
                startIcon={<LoginOutlined />}
                fullWidth
              >
                {isInProgress ? 'Signing in...' : 'Sign in with Microsoft'}
              </Button>

              {configStoreSystemConfig?.enableShibboleth ? (
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleShibbolethSignin}
                  disabled={isInProgress}
                  startIcon={<LoginOutlined />}
                  fullWidth
                >
                  Sign In with InCommon
                </Button>
              ) : null}
            </Box>
          </Paper>
        )}
      </Box>
    </Container>
  );
}

export default signedInOrAnonymousAllowed(HomePage);
